import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  TextInput,
  ActivityIndicator,
  Alert,
  Platform,
  Linking,
} from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { getColors, getShadows } from '../styles/theme';
import {
  getSocialPostDrafts,
  getRecentSocialPosts,
  approveDraftAsAnnouncement,
  rejectDraft,
  updateDraft,
} from '../services/socialSyncService';
import LoadingAnimation from '../components/LoadingAnimation';

export default function SocialDraftReviewScreen({ navigation, route }) {
  const clubId = route.params?.clubId;
  const { showToast } = useToast();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const shadows = getShadows(isDarkMode);

  const [drafts, setDrafts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editContent, setEditContent] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editLocation, setEditLocation] = useState('');

  const fetchDrafts = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const [list, result] = await Promise.all([
        getSocialPostDrafts(clubId, 'pending_review'),
        getRecentSocialPosts(clubId, 5),
      ]);
      setDrafts(list || []);
      setRecentPosts(result?.posts ?? []);
    } catch (e) {
      console.error('fetchDrafts', e);
      showToast?.(e.message || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [clubId, showToast]);

  useFocusEffect(
    useCallback(() => {
      fetchDrafts();
    }, [fetchDrafts])
  );

  const startEdit = (draft) => {
    setEditingId(draft.id);
    setEditTitle(draft.title || '');
    setEditContent(draft.content || '');
    setEditDate(draft.parsed_date ? new Date(draft.parsed_date).toISOString().slice(0, 16) : '');
    setEditLocation(draft.parsed_location || '');
  };

  const saveEdit = async (draftId) => {
    try {
      await updateDraft(draftId, {
        title: editTitle || undefined,
        content: editContent || undefined,
        parsed_date: editDate ? new Date(editDate).toISOString() : null,
        parsed_location: editLocation || null,
      });
      setEditingId(null);
      fetchDrafts();
      showToast?.('Draft updated', 'success');
    } catch (e) {
      showToast?.(e.message || 'Update failed', 'error');
    }
  };

  const handlePublishAnnouncement = async (draft) => {
    setActingId(draft.id);
    try {
      const content = editingId === draft.id ? editContent : draft.content;
      await approveDraftAsAnnouncement(draft.id, { content: content || draft.title });
      showToast?.('Published as announcement', 'success');
      fetchDrafts();
    } catch (e) {
      showToast?.(e.message || 'Publish failed', 'error');
    } finally {
      setActingId(null);
    }
  };

  const handleCreateEventDraft = (draft) => {
    const prefill = {
      title: (editingId === draft.id ? editTitle : draft.title) || 'Event',
      description: (editingId === draft.id ? editContent : draft.content) || '',
      parsedDate: editingId === draft.id && editDate ? editDate : (draft.parsed_date || ''),
      parsedLocation: (editingId === draft.id ? editLocation : draft.parsed_location) || '',
    };
    navigation.navigate('CreateEvent', {
      socialDraftId: draft.id,
      clubId,
      socialDraft: prefill,
    });
  };

  const handleCreateEventFromPost = (post) => {
    navigation.navigate('CreateEvent', {
      clubId,
      socialPost: {
        caption: post.caption || '',
        media_url: post.media_url,
        permalink: post.permalink,
      },
    });
  };

  const handleDismiss = async (draft) => {
    const confirm = Platform.OS === 'web'
      ? window.confirm('Dismiss this draft?')
      : await new Promise((resolve) => {
          Alert.alert('Dismiss', 'Dismiss this draft?', [
            { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
            { text: 'Dismiss', style: 'destructive', onPress: () => resolve(true) },
          ]);
        });
    if (!confirm) return;
    setActingId(draft.id);
    try {
      await rejectDraft(draft.id);
      showToast?.('Draft dismissed', 'success');
      fetchDrafts();
    } catch (e) {
      showToast?.(e.message || 'Failed to dismiss', 'error');
    } finally {
      setActingId(null);
    }
  };

  const confidenceLabel = (score) => {
    if (score == null) return 'Unknown';
    if (score >= 0.7) return 'High';
    if (score >= 0.4) return 'Medium';
    return 'Low';
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
    } catch {
      return iso;
    }
  };

  const dynamicStyles = useMemo(
    () => ({
      container: { backgroundColor: colors.background },
      header: { backgroundColor: colors.surface, borderBottomColor: colors.border },
      headerTitle: { color: colors.text },
      errorText: { color: colors.error },
      emptyStateIcon: { color: colors.textLight },
      emptyStateText: { color: colors.textSecondary },
      emptyStateSubtext: { color: colors.textLight },
      card: { backgroundColor: colors.surface, ...shadows.medium, ...(isDarkMode && { borderWidth: 1, borderColor: colors.border }) },
      sourceText: { color: colors.textSecondary },
      linkText: { color: colors.info },
      confidenceBadge: { backgroundColor: isDarkMode ? colors.border : '#E0E0E0' },
      confidenceHigh: { backgroundColor: isDarkMode ? 'rgba(76, 175, 80, 0.3)' : '#C8E6C9' },
      confidenceLow: { backgroundColor: isDarkMode ? 'rgba(239, 83, 80, 0.3)' : '#FFCDD2' },
      confidenceText: { color: colors.text },
      thumb: { backgroundColor: colors.borderLight },
      input: {
        borderColor: colors.border,
        backgroundColor: isDarkMode ? colors.backgroundDark : '#fff',
        color: colors.text,
      },
      editCancelText: { color: colors.textSecondary },
      title: { color: colors.text },
      meta: { color: colors.textSecondary },
      caption: { color: colors.textSecondary },
      actionLabel: { color: colors.textSecondary },
      primaryBtn: { backgroundColor: colors.primary },
      oneClickHint: { color: colors.primaryDark },
      secondaryBtn: { backgroundColor: isDarkMode ? colors.backgroundDark : '#f5f5f5' },
      secondaryBtnText: { color: colors.text },
      dismissText: { color: colors.error },
      sectionLabel: { color: colors.text },
      sectionHint: { color: colors.textLight },
      recentPostCard: { backgroundColor: colors.surface, borderColor: colors.border },
      recentPostCaption: { color: colors.textSecondary },
      recentPostAction: { color: colors.text },
      recentPostPlaceholder: { backgroundColor: colors.borderLight },
    }),
    [colors, isDarkMode, shadows]
  );

  if (!clubId) {
    return (
      <View style={[styles.container, dynamicStyles.container]}>
        <Text style={[styles.errorText, dynamicStyles.errorText]}>Missing club</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, dynamicStyles.container]}>
      <View style={[styles.header, dynamicStyles.header]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, dynamicStyles.headerTitle]}>Social Posts & Drafts</Text>
      </View>

      {loading ? (
        <View style={styles.loadingContainer}>
          <LoadingAnimation size={200} />
        </View>
      ) : (recentPosts.length === 0 && drafts.length === 0) ? (
        <View style={styles.emptyState}>
          <Icon name="inbox" size={64} color={colors.textLight} />
          <Text style={[styles.emptyStateText, dynamicStyles.emptyStateText]}>No social posts yet</Text>
          <Text style={[styles.emptyStateSubtext, dynamicStyles.emptyStateSubtext]}>
            Connect your Instagram or Facebook to see recent posts here.
          </Text>
        </View>
      ) : (
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
          {/* Recent Social Posts */}
          {recentPosts.length > 0 && (
            <View style={styles.recentPostsSection}>
              <Text style={[styles.sectionLabel, dynamicStyles.sectionLabel]}>Recent Instagram Posts</Text>
              <Text style={[styles.sectionHint, dynamicStyles.sectionHint]}>
                Tap a post to create an event with its image attached
              </Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.recentPostsScroll}>
                {recentPosts.map((post) => (
                  <TouchableOpacity
                    key={post.id}
                    style={[styles.recentPostCard, dynamicStyles.recentPostCard]}
                    onPress={() => handleCreateEventFromPost(post)}
                    activeOpacity={0.7}
                  >
                    {(() => {
                      let payload = post.raw_payload || {};
                      if (typeof payload === 'string') {
                        try {
                          payload = JSON.parse(payload);
                        } catch {
                          payload = {};
                        }
                      }
                      const imageUrl =
                        (payload && typeof payload.thumbnail_url === 'string' && payload.thumbnail_url) ||
                        post.media_url;
                      if (imageUrl) {
                        return (
                          <Image
                            source={{ uri: imageUrl }}
                            style={styles.recentPostImage}
                            resizeMode="cover"
                          />
                        );
                      }
                      return (
                        <View style={[styles.recentPostImage, dynamicStyles.recentPostPlaceholder, { alignItems: 'center', justifyContent: 'center' }]}>
                          <Icon name="image" size={32} color={colors.textLight} />
                        </View>
                      );
                    })()}
                    <Text style={[styles.recentPostCaption, dynamicStyles.recentPostCaption]} numberOfLines={2}>
                      {post.caption || 'No caption'}
                    </Text>
                    <View style={styles.recentPostFooter}>
                      <Icon name="event" size={14} color={colors.primary} />
                      <Text style={[styles.recentPostAction, dynamicStyles.recentPostAction]}>Create event</Text>
                    </View>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}

          {/* Drafts Section */}
          {drafts.length > 0 && (
            <Text style={[styles.sectionLabel, dynamicStyles.sectionLabel, { marginBottom: 12 }]}>
              Pending Drafts ({drafts.length})
            </Text>
          )}
          {drafts.map((draft) => (
            <View key={draft.id} style={[styles.card, dynamicStyles.card]}>
              <View style={styles.cardHeader}>
                <View style={styles.sourceRow}>
                  <Icon
                    name={draft.source_platform === 'facebook' ? 'facebook' : 'camera-alt'}
                    size={18}
                    color={colors.textSecondary}
                  />
                  <Text style={[styles.sourceText, dynamicStyles.sourceText]}>{draft.source_platform || 'Social'}</Text>
                  {draft.source_url && (
                    <TouchableOpacity onPress={() => Linking.openURL(draft.source_url)}>
                      <Text style={[styles.linkText, dynamicStyles.linkText]}>View original</Text>
                    </TouchableOpacity>
                  )}
                </View>
                <View
                  style={[
                    styles.confidenceBadge,
                    dynamicStyles.confidenceBadge,
                    draft.confidence_score >= 0.7 && dynamicStyles.confidenceHigh,
                    draft.confidence_score != null && draft.confidence_score < 0.4 && dynamicStyles.confidenceLow,
                  ]}
                >
                  <Text style={[styles.confidenceText, dynamicStyles.confidenceText]}>
                    {confidenceLabel(draft.confidence_score)}
                  </Text>
                </View>
              </View>

              {(() => {
                const imageUrl = draft.source_image_url || (draft.raw_post && draft.raw_post.media_url);
                if (imageUrl) {
                  return (
                    <Image
                      source={{ uri: imageUrl }}
                      style={[styles.thumb, dynamicStyles.thumb]}
                      resizeMode="cover"
                    />
                  );
                }
                return null;
              })()}

              {editingId === draft.id ? (
                <View style={styles.editForm}>
                  <TextInput
                    style={[styles.input, dynamicStyles.input]}
                    value={editTitle}
                    onChangeText={setEditTitle}
                    placeholder="Title"
                    placeholderTextColor={colors.textLight}
                  />
                  <TextInput
                    style={[styles.input, styles.textArea, dynamicStyles.input]}
                    value={editContent}
                    onChangeText={setEditContent}
                    placeholder="Content"
                    placeholderTextColor={colors.textLight}
                    multiline
                  />
                  <TextInput
                    style={[styles.input, dynamicStyles.input]}
                    value={editDate}
                    onChangeText={setEditDate}
                    placeholder="Date (ISO or local)"
                    placeholderTextColor={colors.textLight}
                  />
                  <TextInput
                    style={[styles.input, dynamicStyles.input]}
                    value={editLocation}
                    onChangeText={setEditLocation}
                    placeholder="Location"
                    placeholderTextColor={colors.textLight}
                  />
                  <View style={styles.editActions}>
                    <TouchableOpacity
                      style={[styles.editSaveBtn, { backgroundColor: colors.primary }]}
                      onPress={() => saveEdit(draft.id)}
                    >
                      <Text style={styles.editSaveText}>Save</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.editCancelBtn} onPress={() => setEditingId(null)}>
                      <Text style={[styles.editCancelText, dynamicStyles.editCancelText]}>Cancel</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              ) : (
                <>
                  <Text style={[styles.title, dynamicStyles.title]}>{draft.title || 'Untitled'}</Text>
                  {draft.parsed_date ? (
                    <Text style={[styles.meta, dynamicStyles.meta]}>Date: {formatDate(draft.parsed_date)}</Text>
                  ) : null}
                  {draft.parsed_location ? (
                    <Text style={[styles.meta, dynamicStyles.meta]}>Location: {draft.parsed_location}</Text>
                  ) : null}
                  <Text style={[styles.caption, dynamicStyles.caption]} numberOfLines={4}>
                    {draft.content || ''}
                  </Text>
                </>
              )}

              <View style={styles.actions}>
                {editingId !== draft.id && (
                  <TouchableOpacity style={styles.editBtn} onPress={() => startEdit(draft)}>
                    <Icon name="edit" size={18} color={colors.textSecondary} />
                    <Text style={[styles.actionLabel, dynamicStyles.actionLabel]}>Edit</Text>
                  </TouchableOpacity>
                )}
                <View style={styles.oneClickBlock}>
                  <TouchableOpacity
                    style={[styles.primaryBtn, dynamicStyles.primaryBtn]}
                    onPress={() => handlePublishAnnouncement(draft)}
                    disabled={!!actingId}
                  >
                    {actingId === draft.id ? (
                      <ActivityIndicator size="small" color={colors.textInverse} />
                    ) : (
                      <Icon name="campaign" size={18} color={colors.textInverse} />
                    )}
                    <Text style={styles.primaryBtnText}>Post to app</Text>
                  </TouchableOpacity>
                  <Text style={[styles.oneClickHint, dynamicStyles.oneClickHint]}>One tap → club announcement</Text>
                </View>
                <TouchableOpacity
                  style={[styles.secondaryBtn, dynamicStyles.secondaryBtn]}
                  onPress={() => handleCreateEventDraft(draft)}
                  disabled={!!actingId}
                >
                  <Icon name="event" size={18} color={colors.text} />
                  <Text style={[styles.secondaryBtnText, dynamicStyles.secondaryBtnText]}>Create event draft</Text>
                </TouchableOpacity>
                <TouchableOpacity style={styles.dismissBtn} onPress={() => handleDismiss(draft)} disabled={!!actingId}>
                  <Text style={[styles.dismissText, dynamicStyles.dismissText]}>Dismiss</Text>
                </TouchableOpacity>
              </View>
            </View>
          ))}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f8f8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  backButton: {
    padding: 8,
    marginRight: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    padding: 20,
    color: '#E53935',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  emptyStateText: {
    fontSize: 18,
    color: '#666',
    marginTop: 12,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: '#999',
    marginTop: 8,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 32,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 4,
    elevation: 2,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  sourceRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  sourceText: {
    fontSize: 12,
    color: '#666',
    marginLeft: 6,
    textTransform: 'capitalize',
  },
  linkText: {
    fontSize: 12,
    color: '#1976D2',
    marginLeft: 12,
  },
  confidenceBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: '#E0E0E0',
  },
  confidenceHigh: {
    backgroundColor: '#C8E6C9',
  },
  confidenceLow: {
    backgroundColor: '#FFCDD2',
  },
  confidenceText: {
    fontSize: 11,
    color: '#333',
    fontWeight: '500',
  },
  thumb: {
    width: '100%',
    height: 160,
    borderRadius: 8,
    marginBottom: 10,
    backgroundColor: '#eee',
  },
  editForm: {
    marginBottom: 10,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 8,
    fontSize: 14,
    color: '#333',
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  editActions: {
    flexDirection: 'row',
    marginTop: 4,
  },
  editSaveBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: '#4CAF50',
    borderRadius: 8,
    marginRight: 8,
  },
  editSaveText: {
    color: '#fff',
    fontWeight: '600',
  },
  editCancelBtn: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  editCancelText: {
    color: '#666',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 6,
  },
  meta: {
    fontSize: 13,
    color: '#666',
    marginBottom: 4,
  },
  caption: {
    fontSize: 13,
    color: '#555',
    marginBottom: 12,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  editBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 10,
  },
  actionLabel: {
    fontSize: 13,
    color: '#666',
    marginLeft: 4,
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: '#4CAF50',
    borderRadius: 8,
  },
  primaryBtnText: {
    color: '#fff',
    fontWeight: '600',
    marginLeft: 6,
  },
  oneClickBlock: {
    marginBottom: 4,
  },
  oneClickHint: {
    fontSize: 11,
    color: '#2E7D32',
    marginTop: 2,
  },
  secondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 14,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },
  secondaryBtnText: {
    color: '#333',
    marginLeft: 6,
  },
  dismissBtn: {
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  dismissText: {
    fontSize: 13,
    color: '#E53935',
  },
  recentPostsSection: {
    marginBottom: 20,
  },
  sectionLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: '#333',
  },
  sectionHint: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
    marginBottom: 12,
  },
  recentPostsScroll: {
    flexDirection: 'row',
  },
  recentPostCard: {
    width: 160,
    marginRight: 12,
    backgroundColor: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#e0e0e0',
  },
  recentPostImage: {
    width: '100%',
    height: 120,
  },
  recentPostCaption: {
    fontSize: 12,
    color: '#555',
    padding: 8,
    lineHeight: 16,
  },
  recentPostFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingBottom: 8,
  },
  recentPostAction: {
    fontSize: 12,
    fontWeight: '600',
    color: '#333',
  },
});
